import { Injectable } from '@angular/core';
import { BaseComponent } from '@wms/app/components';
import { BaseVariableService, VARIABLE_CONSTANTS } from '@wms/variables';
import { isString, StudioUtils } from '@wms/core';
import { includes, remove, keys, forEach, find, lowerFirst, toLower, has, every, isNull } from 'lodash-es';
import { TypeFactoryService } from '@wms/app/services';
import { isObject } from '@wm/core';
import { Subject } from 'rxjs';
import { PreBuiltVariableService } from '@wms/app/services';

@Injectable({
  providedIn: 'root'
})
export class WmsModelVariableService extends BaseComponent {
  propertyGroupChangeSubject = new Subject();
  triggeredSubscription: boolean;
  defaultTypes: Record<string, string> = {
    "string": "LABEL_STRING",
    "boolean": "LABEL_BOOLEAN",
    "number": "LABEL_NUMBER",
    "date": "LABEL_DATE",
    "entry": "LABEL_ENTRY"
  };

  preBuiltTypes: Record<string, string> = {
    "zdays": "LABEL_DAYS",
    "zmonths": "LABEL_MONTHS",
    "zcountry": "LABEL_COUNTRY",
    "zusstates": "LABEL_US_STATES"
  };
  prebuiltCategories: any = {
    'zmonths': 'LABEL_PREBUILT_VARIABLE_MONTHS',
    'zdays': 'LABEL_PREBUILT_VARIABLE_DAYS',
    'zcountry': 'LABEL_PREBUILT_VARIABLE_COUNTRY',
    'zusstate': 'LABEL_PREBUILT_VARIABLE_US_STATES'
  };

  constructor(private baseVariableService: BaseVariableService, private typeFactory: TypeFactoryService, private preBuiltVariableService: PreBuiltVariableService) {
    super();
  }

  /**
 * Initialisation routine called on creation/selection of the variable
 */
  __beforeModelVariableDependencies() {
      this.typeFactory.getAllDataTypes(true , true).then(()=> {
        this.__getVariableDependencies();
      });
  }

  __getVariableDependencies(){
    this.__getExternalTypes();
    let variable = this.baseVariableService.selectedVariable.variableObject;
    // for login action, initialize the default input options.
    if (variable.category === 'wm.LoginVariable') {
      variable.dataSet = { j_username: "", j_password: "", j_rememberme: false };
    }
    /* create bindings for the variable */
    this.__updateBindingInfo();
  }

  __getExternalTypes() {
    let externalTypes: any = [];
    let currType;
    /* has to be performed only for static variables */
    if (this.baseVariableService.selectedVariable.category !== "wm.Variable") {
      return;
    }
    this.baseVariableService.selectedVariable.propertyObj.type.options = [];
    this.baseVariableService.selectedVariable.propertyObj.type.propertyName = 'name';
    this.baseVariableService.selectedVariable.propertyObj.type.modelPropertyName = 'value';

    // Append default types
    forEach(this.defaultTypes, (typeLabel) => {
      currType = this.locale[typeLabel];
      this.baseVariableService.selectedVariable.propertyObj.type.options.push({
        'category': 'Default',
        'name': currType,
        'value': lowerFirst(currType)
      });
    });

    // Append Service types
    forEach(this.typeFactory.dataTypes, (typeObj, typeLabel) => {
      if (isString(typeObj.service)) {
        externalTypes.push(typeLabel);
      }
    });
    externalTypes.sort();
    forEach(externalTypes, (val) => {
      this.baseVariableService.selectedVariable.propertyObj.type.options.push({
        'category': 'Services',
        'name': StudioUtils.getUpdatedPropertyName(val),
        'value': val
      });
    });

    // Append prebuilt categories
    forEach(this.prebuiltCategories, (label, key) => {
      //$scope.categoryFilterList[key] = label;
      this.baseVariableService.selectedVariable.propertyObj.type.options.push({
        'category': this.locale.LABEL_PREBUILT_VARIABLE,
        'name': this.locale[label],
        'value': key
      });
    });
  };

  __onPropertyChange(propertyName: string, value?: any) {
    let variable = this.baseVariableService.selectedVariable.variableObject;
    let skipBindingInfoUpdate: boolean = false;
    this.baseVariableService.markUpdated();

    let propertyValue: any = typeof(value) === 'object' ? value.value : variable[propertyName];

    switch (propertyName) {
      case 'isList':
        if (variable[propertyName]) {
          /* if isList type make dataset an array only its not an array */
          if (!Array.isArray(variable.dataSet)) {
            variable.dataSet = [variable.dataSet];
          }
        } else {
          /* else make it an object*/
          variable.dataSet = variable.dataSet[0];
        }
        this.baseVariableService.jsonTextEditor = { ...this.baseVariableService.jsonTextEditor, value: JSON.stringify(variable.dataSet,undefined,2), language: "json" };
        return;
      case 'type':
        switch (toLower(variable[propertyName])) {
          case 'entry':
            /* if variable type is entry insert additional name key */
            variable.dataSet = {
              "name": "name0",
              "dataValue": "dataValue0"
            };
            break;
          case 'string':
            variable.dataSet = {
              dataValue: ''
            };
            break;
          case 'number':
            variable.dataSet = {
              dataValue: 0
            };
            break;
          case 'boolean':
            variable.dataSet = {
              dataValue: true
            };
            break;
          case 'date':
            variable.dataSet = {
              dataValue: new Date().toLocaleDateString()
            };
            this.baseVariableService.onDateChangeSubject.next();
            break;
          default:
            if (has(this.prebuiltCategories, propertyValue)) {
              this.preBuiltVariableService.getData().then((response: any) => {
                let data: any = response.default;
                if (data) {
                  variable.dataSet = data[propertyValue];
                  /* update the bindings based on the new type */
                  this.__updateBindingInfo(true);
                  variable.isList = true;
                }
              });
            } else {
              variable.dataSet = StudioUtils.getTypeModel(propertyValue);
              skipBindingInfoUpdate = true;
              this.__updateBindingInfo();
            }
        }

        if (!has(this.prebuiltCategories, propertyValue)) {
          /* update the bindings based on the new type */
          if (!skipBindingInfoUpdate)
            this.__updateBindingInfo(true);

          /* if variable is listType, make dataset an array */
          if (variable.isList) {
            variable.dataSet = [variable.dataSet];
          }
          this.baseVariableService.jsonTextEditor = { ...this.baseVariableService.jsonTextEditor, value: JSON.stringify(variable.dataSet,undefined,4), language: "json" };
        }
        return;
    }

    /*
     * executing the event change method to create callback variables.
     * this is a fallback handler for the variables not having their own propertychange handlers (like login variable)
     */
    if (VARIABLE_CONSTANTS.EVENTS.indexOf(propertyName) > -1) {
      this.baseVariableService.onEventChange(propertyName, value);
    }
    const isEmptyVal = every(value, v => v === '' || isNull(v));

    if ((variable.category === 'wm.LoginVariable' || variable.category === 'wm.TimerVariable') && propertyName === 'spinnerContext' && isEmptyVal && !this.triggeredSubscription) {
      this.triggeredSubscription = true;
      this.propertyGroupChangeSubject.next(true);
      return;
    } else if (!isEmptyVal) {
      this.triggeredSubscription = false;
      this.propertyGroupChangeSubject.next(true);
    }
  };


  __isPrimitiveType(type: any) {
    return isString(this.defaultTypes[type?.toLowerCase()]);
  }

  __isPreBuiltType(type: any) {
    return isString(this.preBuiltTypes[type]);
  }

  /**
   * updates the input-binding fields for the variable according to its type
   * called on change of the type of the variable in variable creation screen
   * @param initBindings flag to determine whether dataBindings to be re-initialised
   */
  __updateBindingInfo(initBindings?: any) {
    let variable = this.baseVariableService.selectedVariable.variableObject,
      type = variable.type,
      isPrimitive = this.__isPrimitiveType(type),
      isPreBuilt = this.__isPreBuiltType(type);

    /*TODO: to remove this statement after discussion on keeping nested type info while opening dataBinding tree for static varaible */
    this.baseVariableService.selectedVariable.isPrimitiveType = isPrimitive;
    this.baseVariableService.selectedVariable.isPreBuiltType = isPreBuilt;


    /* if selectedVariable is of type primitive then jsonTextEditor view is not applicable */
    if(this.baseVariableService.selectedVariable.isPrimitiveType && this.baseVariableService.jsonEditorViewOption === 2){
      this.baseVariableService.jsonEditorViewOption = 1;
    }

    if (initBindings) {
      variable.dataBinding = [];
    }
    if (isPrimitive) {
      this.__getBindingsByDataSet();
    } else {
      this.__getBindingsByType(initBindings);
    }

    // The typeahead options are not showing up when the model value is cleared, as it is being reset. - so added below check
    if (type) {
      /* add the created bindings to the properties object for display in DATA tab */
      this.baseVariableService.addBindingsToProperties();
    }
  }

  /**
   * Creates binding objects for a variable based on the dataSet
   * Used for static variables of primitive types
   */
  __getBindingsByDataSet() {
    /* if dataSet is an listType, get the first object out of the dataSet */
    let variable = this.baseVariableService.selectedVariable.variableObject,
      dataSet = variable.dataSet;

    dataSet = Array.isArray(dataSet) ? dataSet[0] : dataSet;
    /*Removing objects those no longer exist in dataset*/
    this.__removeDeletedNodes(keys(dataSet));

    if (isObject(dataSet) && !Array.isArray(dataSet)) {
      forEach(dataSet, (field, fieldName) => {
        this.__createBindingObjectIfNotExists(fieldName);
      });
    }
  }

  /**
   * Checks for binding object in existing bindings of a variable, if not found creates one
   * @param targetFieldName
   * @param type
   */
  __createBindingObjectIfNotExists(targetFieldName: any, type?: any) {
    let existingBindings = this.baseVariableService.selectedVariable.variableObject.dataBinding,
      curFieldObj = find(existingBindings, { target: targetFieldName });

    /* if binding not found, push it */
    if (!curFieldObj) {
      existingBindings.push({
        "target": targetFieldName,
        "type": type
      });
    }
  }

  /**
   * Creates binding objects for a variable based on the dataType
   * Used for static variables of non-primitive types like types generated for a Service
   * @param initBindings will be true only for the first time
   */
  __getBindingsByType(initBindings: any) {
    let variable = this.baseVariableService.selectedVariable.variableObject,
      variableType = variable.type,
      dataSet = variable.dataSet;

    dataSet = Array.isArray(dataSet) ? dataSet[0] : dataSet;
    /*Removing objects those no longer exist in dataset*/
    this.__removeDeletedNodes(keys(dataSet));

    /*Adding nodes to dataBinding for the keys in dataTypes only for the first time*/
    let dataTypes = this.typeFactory.getTypes();
    if (dataTypes[variableType] && initBindings) {
      forEach(dataTypes[variableType].fields, (field, fieldName) => {
        this.__createBindingObjectIfNotExists(fieldName, field.type);
      });
    } else {
      forEach(dataSet, (field, fieldName) => {
        this.__createBindingObjectIfNotExists(fieldName);
      });
    }
  }

  /**
   * Removes objects from data binding whose value is not bound and target doesn't exist in keys passed
   * @param keys
   */
  __removeDeletedNodes(keys: any) {
    let variable = this.baseVariableService.selectedVariable.variableObject;
    remove(variable.dataBinding, (obj: any) => {
      return !includes(obj.value, 'bind:') && !includes(keys, obj.target);
    });
  }
}
